import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiGrid, FiUsers, FiBell, FiCalendar, FiImage, FiAward, FiMessageCircle,
  FiLogOut, FiMenu, FiX, FiChevronRight, FiUser, FiSettings
} from 'react-icons/fi';
import { MdSchool } from 'react-icons/md';
import { useAuth } from '../../context/AuthContext';
import { contactAPI } from '../../services/api';

const navItems = [
  { label: 'Dashboard', path: '/admin', icon: FiGrid, exact: true },
  { label: 'Admissions', path: '/admin/admissions', icon: FiUsers },
  { label: 'Notices', path: '/admin/notices', icon: FiBell },
  { label: 'Events', path: '/admin/events', icon: FiCalendar },
  { label: 'Gallery', path: '/admin/gallery', icon: FiImage },
  { label: 'Results', path: '/admin/results', icon: FiAward },
  { label: 'Contact Messages', path: '/admin/contacts', icon: FiMessageCircle },
  { label: 'Site Settings', path: '/admin/settings', icon: FiSettings },
];

const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadContacts, setUnreadContacts] = useState(0);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    contactAPI.getAll().then((res) => {
      setUnreadContacts(res.data?.data?.unreadCount || 0);
    }).catch(() => {});
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-6 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-secondary-500 rounded-xl flex items-center justify-center flex-shrink-0">
            <MdSchool className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="font-serif font-bold text-white text-sm">Gargi Academy</h2>
            <p className="text-primary-200 text-xs">Admin Panel</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {navItems.map((item, idx) => (
          <NavLink
            key={idx}
            to={item.path}
            end={item.exact}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-xl transition-all text-sm font-medium group ${
                isActive
                  ? 'bg-secondary-500 text-white shadow-lg'
                  : 'text-primary-200 hover:bg-white/10 hover:text-white'
              }`
            }
            onClick={() => setSidebarOpen(false)}
          >
            <item.icon className="w-5 h-5 flex-shrink-0" />
            {item.label}
            {item.path === '/admin/contacts' && unreadContacts > 0 && (
              <span className="ml-auto bg-secondary-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0">
                {unreadContacts > 9 ? '9+' : unreadContacts}
              </span>
            )}
            {item.path !== '/admin/contacts' && (
              <FiChevronRight className="w-4 h-4 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
            )}
          </NavLink>
        ))}
      </nav>

      {/* User Info */}
      <div className="p-4 border-t border-white/10">
        <div className="flex items-center gap-3 px-4 py-3 bg-white/5 rounded-xl mb-2">
          <div className="w-9 h-9 bg-secondary-500/20 rounded-lg flex items-center justify-center">
            <FiUser className="w-5 h-5 text-secondary-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-medium truncate">{user?.name || 'Admin'}</p>
            <p className="text-primary-200 text-xs truncate">{user?.email || 'admin@school.com'}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 w-full px-4 py-2.5 text-red-400 hover:bg-red-500/10 rounded-xl transition-colors text-sm font-medium"
        >
          <FiLogOut className="w-4 h-4" />
          Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 bg-primary-500 flex-shrink-0">
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-black/50 z-40"
              onClick={() => setSidebarOpen(false)}
            />
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: 'spring', damping: 25 }}
              className="lg:hidden fixed left-0 top-0 bottom-0 w-64 bg-primary-500 z-50 flex flex-col"
            >
              <SidebarContent />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Header */}
        <header className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden p-2 text-gray-500 hover:text-primary-500 hover:bg-gray-100 rounded-lg transition-colors"
              onClick={() => setSidebarOpen(true)}
            >
              <FiMenu className="w-5 h-5" />
            </button>
            <div>
              <h1 className="font-serif font-bold text-primary-500 text-lg">Admin Dashboard</h1>
              <p className="text-gray-500 text-xs hidden sm:block">
                Welcome back, {user?.name || 'Admin'}
              </p>
            </div>
          </div>
          <div className="w-9 h-9 bg-primary-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
            {user?.name?.[0]?.toUpperCase() || 'A'}
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
